import * as XLSX from "xlsx-js-style";
import dayjs, { type Dayjs } from "dayjs";
import { IPayrollReport } from "../models/Payroll.ts";
import type { PayrollRowDetailsSnapshot } from "../services/payroll.service.ts";
import {
  buildCuadroExtendedExportRowValues,
  getCuadroExtendedExcelSubheaderLabels,
  getCuadroExtendedDeduccionesColSpan,
  getCuadroExtendedExcelPercepcionesGroupColSpan,
  isExtendedEmptyDisplay,
  type CuadroExtendedColumnSchema,
} from "./cuadroExtendedBreakdown.ts";
import { getPayrollReceiptPeriodInfo } from "./payrollUtils.ts";

const PAYROLL_COMPANY_NAME = "Grupo Chili Guajili";

const GROUP_COLORS = {
  percepciones: "50B7C0",
  deducciones: "F43F5E",
  otros: "0EA5E9",
  final: "EAB308",
} as const;

const DOC_TITLE_STYLE = {
  font: { bold: true, color: { rgb: "FFFFFF" }, sz: 14 },
  fill: { fgColor: { rgb: "15225D" } },
  alignment: { horizontal: "center", vertical: "center" },
};

const DOC_LABEL_STYLE = {
  font: { bold: true, color: { rgb: "FFFFFF" }, sz: 9 },
  fill: { fgColor: { rgb: "2F5597" } },
  alignment: { horizontal: "left", vertical: "center" },
};

const DOC_VALUE_STYLE = {
  font: { color: { rgb: "334155" }, sz: 9 },
  fill: { fgColor: { rgb: "F8FAFC" } },
  alignment: { horizontal: "left", vertical: "center" },
};

const SUBHEADER_STYLE = {
  font: { bold: true, color: { rgb: "475569" }, sz: 7 },
  fill: { fgColor: { rgb: "F8FAFC" } },
  alignment: {
    horizontal: "center",
    vertical: "center",
    wrapText: false,
  },
  border: {
    bottom: { style: "thin", color: { rgb: "E2E8F0" } },
  },
};

const GROUP_HEADER_STYLE = (color: string) => ({
  font: { bold: true, color: { rgb: "FFFFFF" }, sz: 8 },
  fill: { fgColor: { rgb: color } },
  alignment: { horizontal: "center", vertical: "center" },
});

const DOCUMENT_HEADER_ROW_COUNT = 8;

function columnWidthForExcel(index: number, label: string): number {
  if (index === 0) return 18;
  if (index === 1) return 14;
  if (label.startsWith("$") || label.includes("Tot.") || label.includes("Total")) {
    return 10;
  }
  if (
    label.startsWith("Bono") ||
    label === "Efectivo" ||
    label === "Pago tarj." ||
    label === "Prima vac."
  ) {
    return 9;
  }
  if (label.length <= 6) return 7;
  return Math.min(Math.max(label.length, 8), 11);
}

function cellStyleForValue(
  value: string,
  rowIndex: number,
  colIndex: number
): Record<string, unknown> {
  const zebra = rowIndex % 2 === 0 ? "FFFFFF" : "F8FAFC";
  const isEmpty = isExtendedEmptyDisplay(value);
  const isTextCol = colIndex === 0 || colIndex === 1;

  return {
    font: {
      sz: 8,
      color: { rgb: isEmpty ? "94A3B8" : "1E293B" },
    },
    fill: { fgColor: { rgb: zebra } },
    alignment: {
      horizontal: isTextCol ? "left" : "center",
      vertical: "center",
      wrapText: false,
    },
    border: {
      bottom: { style: "thin", color: { rgb: "E2E8F0" } },
    },
  };
}

function setCell(
  sheet: XLSX.WorkSheet,
  row: number,
  col: number,
  value: string,
  style?: Record<string, unknown>
) {
  const ref = XLSX.utils.encode_cell({ r: row, c: col });
  sheet[ref] = { v: value, t: "s", s: style };
}

function addDocumentHeader(
  sheet: XLSX.WorkSheet,
  merges: XLSX.Range[],
  totalCols: number,
  options: {
    branchName: string;
    companyName: string;
    periodLabel: string;
    downloadDateLabel: string;
    totalCollaborators: number;
  }
) {
  const valueMergeEnd = Math.min(4, totalCols - 1);

  setCell(sheet, 0, 0, "CUADRO FINANZAS - NÓMINA", DOC_TITLE_STYLE);
  if (totalCols > 1) {
    merges.push({
      s: { r: 0, c: 0 },
      e: { r: 0, c: totalCols - 1 },
    });
  }

  const infoRows: [string, string][] = [
    ["EMPRESA:", options.companyName],
    ["SUCURSAL:", options.branchName],
    ["PERIODO DE PAGO:", options.periodLabel],
    ["TOTAL COLABORADORES:", String(options.totalCollaborators)],
    ["FECHA DE DESCARGA:", options.downloadDateLabel],
  ];

  infoRows.forEach(([label, value], index) => {
    const row = 2 + index;
    setCell(sheet, row, 0, label, DOC_LABEL_STYLE);
    setCell(sheet, row, 1, value, DOC_VALUE_STYLE);
    if (valueMergeEnd > 1) {
      merges.push({
        s: { r: row, c: 1 },
        e: { r: row, c: valueMergeEnd },
      });
    }
  });
}

export type ExportCuadroExtendedExcelInput = {
  records: IPayrollReport[];
  rowDetailsByPartnerId: Record<number, PayrollRowDetailsSnapshot>;
  columnSchema: CuadroExtendedColumnSchema;
  fullDayHours: number;
  dates: { start: Dayjs; end: Dayjs };
  branchName?: string;
  companyName?: string;
};

export function exportCuadroExtendedExcel(
  input: ExportCuadroExtendedExcelInput
): void {
  const {
    records,
    rowDetailsByPartnerId,
    columnSchema,
    fullDayHours,
    dates,
    branchName,
    companyName = PAYROLL_COMPANY_NAME,
  } = input;

  const periodInfo = getPayrollReceiptPeriodInfo(dates);
  const resolvedBranchName =
    branchName?.trim() || records[0]?.branch?.trim() || "Sin sucursal";

  const { bonusColumns, deductionColumns } = columnSchema;
  const subheaders = getCuadroExtendedExcelSubheaderLabels(
    bonusColumns,
    deductionColumns
  );
  const totalCols = subheaders.length;

  const percepcionesSpan = getCuadroExtendedExcelPercepcionesGroupColSpan(
    bonusColumns.length
  );
  const deduccionesSpan = getCuadroExtendedDeduccionesColSpan(
    deductionColumns.length
  );
  const otrosSpan = 2;
  const finalSpan = 1;

  const sheet: XLSX.WorkSheet = {};
  const merges: XLSX.Range[] = [];

  addDocumentHeader(sheet, merges, totalCols, {
    branchName: resolvedBranchName,
    companyName,
    periodLabel: periodInfo.periodLabel,
    downloadDateLabel: dayjs().format("DD-MM-YYYY HH:mm"),
    totalCollaborators: records.length,
  });

  const groupRow = DOCUMENT_HEADER_ROW_COUNT;
  const subheaderRow = DOCUMENT_HEADER_ROW_COUNT + 1;
  const dataStartRow = DOCUMENT_HEADER_ROW_COUNT + 2;

  let col = 0;
  const pushGroup = (label: string, span: number, color: string) => {
    setCell(sheet, groupRow, col, label, GROUP_HEADER_STYLE(color));
    if (span > 1) {
      merges.push({
        s: { r: groupRow, c: col },
        e: { r: groupRow, c: col + span - 1 },
      });
    }
    col += span;
  };

  pushGroup("PERCEPCIONES", percepcionesSpan, GROUP_COLORS.percepciones);
  pushGroup("DEDUCCIONES", deduccionesSpan, GROUP_COLORS.deducciones);
  pushGroup("OTROS", otrosSpan, GROUP_COLORS.otros);
  pushGroup("FINAL", finalSpan, GROUP_COLORS.final);

  subheaders.forEach((label, index) => {
    setCell(sheet, subheaderRow, index, label, SUBHEADER_STYLE);
  });

  records.forEach((record, rowOffset) => {
    const snapshot = rowDetailsByPartnerId[record.partnerId];
    const values = buildCuadroExtendedExportRowValues({
      record,
      snapshot: snapshot
        ? {
            paymentDetail: snapshot.paymentDetail,
            chartDaysWorked: snapshot.chartDaysWorked,
          }
        : null,
      fullDayHours,
      bonusColumns,
      deductionColumns,
    });

    const excelRow = dataStartRow + rowOffset;
    values.forEach((value, colIndex) => {
      setCell(
        sheet,
        excelRow,
        colIndex,
        value,
        cellStyleForValue(value, rowOffset, colIndex)
      );
    });
  });

  const lastRow = Math.max(dataStartRow + records.length - 1, subheaderRow);
  sheet["!ref"] = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: lastRow, c: totalCols - 1 },
  });
  sheet["!merges"] = merges;
  sheet["!cols"] = subheaders.map((label, index) => ({
    wch: columnWidthForExcel(index, label),
  }));
  sheet["!rows"] = [
    { hpt: 28 },
    { hpt: 6 },
    { hpt: 18 },
    { hpt: 18 },
    { hpt: 18 },
    { hpt: 18 },
    { hpt: 18 },
    { hpt: 8 },
    { hpt: 16 },
    { hpt: 18 },
    ...records.map(() => ({ hpt: 15 })),
  ];

  const workbook = XLSX.utils.book_new();
  const sheetTitle = resolvedBranchName
    ? `Finanzas ${resolvedBranchName}`.slice(0, 31)
    : "Cuadro Finanzas";
  XLSX.utils.book_append_sheet(workbook, sheet, sheetTitle);

  const safePeriod = `${periodInfo.startDateDisplay}_${periodInfo.endDateDisplay}`.replace(
    /\//g,
    "-"
  );
  XLSX.writeFile(workbook, `Cuadro_Finanzas_${safePeriod}.xlsx`);
}
